<script setup>
import { visualMode, hasLottie, showHeroVideo } from '~/utils/block-visual.js'
  
// Imports
import { ref, computed, onMounted, nextTick } from 'vue'
import { getSwiper } from '@/utils/swiper-loader'
import SlideLottie from './slideLottie.vue'

// Props
const props = defineProps({
  slides: {
    type: Array,
    default: () => []
  }
})

// Refs
const swiperNavRef = ref(null)
const swiperRef = ref(null)
const activeSlideIndex = ref(0)

// State
const isSwiperInitialized = ref(false)
const prefersReducedMotion = ref(false)
const isAutoplayEnabled = computed(() => !prefersReducedMotion.value)
const hasCompositions = computed(() => props.slides.some(slide => (visualMode(slide) === 'kv' && slide.conversationBox?.length)))
const totalSlides = computed(() => props.slides.length || 0)
const carouselStatusText = computed(() => {
  if (!totalSlides.value) return ''
  return `Slide ${activeSlideIndex.value + 1} of ${totalSlides.value}`
})
let swiperInstance = null
let swiperNav = null
let progressAnimationId = null
let progressInterval = null
const AUTOPLAY_DELAY = 7500
let initPromise = null

const goToSlide = (index) => {
  if (!isSwiperInitialized.value || !swiperInstance) return
  
  // Use slideToLoop for loop mode
  if (swiperInstance.slideToLoop) {
    swiperInstance.slideToLoop(index)
  } else if (swiperInstance.slideTo) {
    swiperInstance.slideTo(index)
  }
}

const handleNavButtonKeydown = (event, index) => {
  const key = event.key
  const code = event.code
  const keyCode = event.keyCode

  if (key === 'Enter' || code === 'Enter' || keyCode === 13) {
    event.preventDefault()
    event.stopPropagation()
    goToSlide(index)
    return
  }

  if (key === ' ' || key === 'Spacebar' || code === 'Space' || keyCode === 32) {
    event.preventDefault()
    event.stopPropagation()
    goToSlide(index)
    return
  }

  if (!totalSlides.value) return

  if (key === 'ArrowRight' || key === 'Right' || code === 'ArrowRight' || keyCode === 39) {
    event.preventDefault()
    event.stopPropagation()
    goToSlide((index + 1) % totalSlides.value)
    return
  }

  if (key === 'ArrowLeft' || key === 'Left' || code === 'ArrowLeft' || keyCode === 37) {
    event.preventDefault()
    event.stopPropagation()
    goToSlide((index - 1 + totalSlides.value) % totalSlides.value)
  }
}

const initSwiper = async () => {
  if (!swiperNavRef.value || !swiperRef.value || isSwiperInitialized.value || props.slides.length < 2) return
  if (initPromise) return initPromise

  initPromise = (async () => {
    const { Swiper, modules } = await getSwiper(['Autoplay', 'Thumbs', 'FreeMode'])
    if (!swiperNavRef.value || !swiperRef.value || isSwiperInitialized.value || props.slides.length < 2) return

    swiperNav = new Swiper(swiperNavRef.value, {
      slidesPerView: 'auto',
      freeMode: true,
      watchSlidesProgress: true,
      modules,
    })

    swiperInstance = new Swiper(swiperRef.value, {
      loop: true,
      // HTML compositions can reflow vertically on mobile; legacy slides retain their own ratio.
      autoHeight: hasCompositions.value,
      spaceBetween: 8,
      autoplay: isAutoplayEnabled.value
        ? {
            delay: AUTOPLAY_DELAY,
            disableOnInteraction: false
          }
        : false,
      thumbs: {
        swiper: swiperNav,
      },
      modules,
      on: {
        slideChange: (swiper) => {
          // Get the real index (accounting for loop)
          const realIndex = swiper.realIndex
          activeSlideIndex.value = realIndex
          resetProgressAnimation()
          if (isAutoplayEnabled.value) {
            nextTick(() => {
              startProgressAnimation()
            })
          }
        },
        autoplayStart: () => {
          if (!isAutoplayEnabled.value) return
          nextTick(() => {
            startProgressAnimation()
          })
        },
        autoplayTimeLeft: (swiper, timeLeft, percentage) => {
          if (!isAutoplayEnabled.value) return
          updateProgressBar(1 - percentage)
        }
      }
    })

    // Set initial active slide index and start progress animation
    activeSlideIndex.value = swiperInstance.realIndex
    if (isAutoplayEnabled.value) {
      nextTick(() => {
        startProgressAnimation()
      })
    }

    isSwiperInitialized.value = true
  })()

  return initPromise
}

const updateProgressBar = (progress) => {
  if (!swiperNavRef.value) return

  const activeThumb = swiperNavRef.value.querySelector('.swiper-slide-thumb-active')
  if (!activeThumb) return

  const progressBar = activeThumb.querySelector('.carousel-thumbs__progress')
  if (!progressBar) return

  progressBar.style.width = `${progress * 100}%`
}

const startProgressAnimation = () => {
  if (!swiperInstance || !swiperNavRef.value || !isAutoplayEnabled.value) return

  stopProgressAnimation()

  // Reset progress bar
  updateProgressBar(0)

  // Use requestAnimationFrame to smoothly animate progress
  let startTime = Date.now()

  const animate = () => {
    const elapsed = Date.now() - startTime
    const progress = Math.min(elapsed / AUTOPLAY_DELAY, 1)

    updateProgressBar(progress)

    if (progress < 1) {
      progressAnimationId = requestAnimationFrame(animate)
    }
  }

  // Small delay to ensure DOM is ready
  setTimeout(() => {
    startTime = Date.now()
    progressAnimationId = requestAnimationFrame(animate)
  }, 50)
}

const stopProgressAnimation = () => {
  if (progressAnimationId) {
    cancelAnimationFrame(progressAnimationId)
    progressAnimationId = null
  }
  if (progressInterval) {
    clearInterval(progressInterval)
    progressInterval = null
  }
}

const resetProgressAnimation = () => {
  if (!swiperNavRef.value) return

  const allThumbs = swiperNavRef.value.querySelectorAll('.carousel-thumbs__nav-item')
  allThumbs.forEach(thumb => {
    const progressBar = thumb.querySelector('.carousel-thumbs__progress')
    if (progressBar) {
      progressBar.style.animation = 'none'
      progressBar.style.width = '0'
    }
  })
}

// Lifecycle
onMounted(() => {
  // Respect user preference for reduced motion
  if (typeof window !== 'undefined' && 'matchMedia' in window) {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    prefersReducedMotion.value = mediaQuery.matches
  }

  // Initialize swiper in nextTick to ensure DOM is ready
  // No need for IntersectionObserver as this section is always visible at the top of the page
  nextTick(() => {
    initSwiper()
  })
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<section class="carousel"
    role="region"
    aria-label="Hero carousel">
  <span class="sr-only" aria-live="polite">{{ carouselStatusText }}</span>
  <div class="carousel-thumbs__swiper-nav"
      role="group"
      aria-label="Carousel navigation">
    <div ref="swiperNavRef" class="swiper">
      <div class="swiper-wrapper flex" role="list">

        <div v-if="slides.length > 1"
            v-for="(slide, index) in slides"
            :key="slide.id"
            class="carousel-thumbs__nav-item swiper-slide"
            role="listitem">
          <button 
              type="button"
              class="carousel-thumbs__nav-item-button"
              :id="`hero-carousel-nav-${slide.id}`"
              :aria-label="`Go to slide ${index + 1}: ${slide.title}`"
              :aria-controls="`hero-card-${slide.id}`"
              :aria-current="activeSlideIndex === index ? 'true' : undefined"
              @click="goToSlide(index)"
              @keydown="handleNavButtonKeydown($event, index)">
            <div class="carousel-thumbs__nav-item-img" aria-hidden="true">
              <GlobalsImage
                :image="(slide.thumbnail || [])[0] || null"
                :max-width-mobile="100"
                :max-width="200"
                :quality="50"
            />
            </div>
            <div class="carousel-thumbs__progress" aria-hidden="true"></div>
            <p class="carousel-thumbs__nav-item-name">
              {{ slide.title }}
            </p>
          </button>
        </div>

      </div>
    </div>
  </div>

  <div ref="swiperRef" 
      class="carousel-thumbs__cards swiper relative"
      role="group"
      aria-roledescription="carousel"
      aria-label="Hero slides"
      aria-live="off">
    <div class="swiper-wrapper flex">
      <div v-for="(slide, index) in slides"
        :key="slide.id"
        :id="`hero-card-${slide.id}`"
        class="carousel-thumbs__card swiper-slide"
        :class="{ '--conversation': (visualMode(slide) === 'kv' && slide.conversationBox?.length) }"
        role="group"
        aria-roledescription="slide"
        :aria-label="`Slide ${index + 1} of ${slides.length}: ${slide.title}`"
        style="width: 100%; flex-shrink: 0;"
      >
        <GlobalsConversationImage
          v-if="(index === 0 || isSwiperInitialized) && (visualMode(slide) === 'kv' && slide.conversationBox?.length)"
          :entry="slide"
        />
        <GlobalsImage
          v-else-if="index === 0 || isSwiperInitialized"
          :landscape="(slide.imageDesktop || [])[0] || null"
          :portrait="(slide.imageMobile || [])[0] || null"
          :alt="slide.title || `Hero slide ${index + 1}`"
          :loading="index === 0 ? 'eager' : 'lazy'"
          :quality="70"
        />

        <!-- Optional Lottie over the image, centred; one file fills both slots -->
        <SlideLottie
          v-if="visualMode(slide) === 'lottie' && (index === 0 || isSwiperInitialized) && hasLottie(slide)"
          :desktop-url="slide.lottieFileDesktop?.[0]?.url || ''"
          :mobile-url="slide.lottieFileMobile?.[0]?.url || ''"
          :active="index === activeSlideIndex"
        />

        <div v-if="showHeroVideo(slide) && slide.assetDesktop?.[0]?.url"
          class="carousel-thumbs__card-desktop-video">
          <GlobalsVideo
            :video="slide.assetDesktop"
            :controls="false"
          />
        </div>

        <div v-if="showHeroVideo(slide) && slide.assetMobile?.[0]?.url"
          class="carousel-thumbs__card-mobile-video">
          <GlobalsVideo
            :video="slide.assetMobile"
            :controls="false"
          />
        </div>
      
      </div>
    </div>
  </div>

</section>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.carousel {
  overflow: hidden;
  position: relative;
}

.carousel-thumbs__swiper-nav {
  position: absolute;
  bottom: 16px;
  left: 16px;
  z-index: 1;
}

.carousel-thumbs__nav-item {
  position: relative;
  width: 44px;
  height: 44px;
  transition: all 0.3s ease;
}

.carousel-thumbs__nav-item-button {
  display: flex;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  outline: 1px solid rgba(255, 255, 255, 0.50);
  transition: all 0.3s ease;
  padding: 7px 9px;
  position: relative;
  overflow: hidden;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  text-align: left;

  &:hover {
    outline-color: var(--color-white);
  }

  &:focus-visible {
    outline: 2px solid var(--color-white);
    outline-offset: 2px;
  }
}

.carousel-thumbs__nav-item + .carousel-thumbs__nav-item {
  margin-left: 4px;
}

.carousel-thumbs__nav-item-name {
  width: calc(118px - 18px);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 500;
  line-height: 100%;
  letter-spacing: -0.24px;
  color: var(--color-white);
  transition: all 0.5s ease;
  opacity: 0;
  position: relative;
  z-index: 3;
}

.carousel-thumbs__nav-item-img {
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: inherit;
}

.carousel-thumbs__progress {
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  opacity: 0;
  z-index: 1;
  pointer-events: none;
  transition: opacity 0.3s ease;
  border-radius: 0;
  will-change: width;
}

.carousel-thumbs__nav-item.swiper-slide-thumb-active {
  width: 120px;

  .carousel-thumbs__nav-item-button {
    outline: 2px solid var(--color-white);

    &::after {
      width: 0;
      opacity: 0;
    }
  }

  .carousel-thumbs__nav-item-name {
    opacity: 1;
  }

  .carousel-thumbs__progress {
    opacity: 0.6;
  }
}

.carousel-thumbs__cards .swiper-wrapper {
  margin: 0;
  padding: 0;
}

.carousel-thumbs__card {
  /* Reserve space to prevent layout shifts on initial load */
  width: 100%;
  aspect-ratio: 398 / 444.42; /* ~0.896 (width / height) for mobile */
  max-height: 700px;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
}

@media (min-width: 1024px) {
  .carousel-thumbs__card {
    /* Desktop aspect ratio based on original calc(510 / 1552 * (100vw - 64px)) */
    aspect-ratio: 1552 / 510; /* ~3.045 (width / height) */
    max-height: 510px;
  }

  .carousel-thumbs__swiper-nav {
    bottom: 24px;
    left: 24px;
  }

  .carousel-thumbs__nav-item {
    width: 72px;
    height: 72px;
  }

  .carousel-thumbs__nav-item-button {
    padding: 11px;
    border-radius: 8px;

    &:hover, &:focus-visible {
      outline: 2px solid var(--color-white);
    }
  }

  .carousel-thumbs__nav-item + .carousel-thumbs__nav-item {
    margin-left: 8px;
  }

  .carousel-thumbs__nav-item-name {
    min-width: calc(178px - 22px);
    font-size: 13px;
    letter-spacing: -0.26px;
  }

  .carousel-thumbs__nav-item.swiper-slide-thumb-active {
    width: 180px;

    .carousel-thumbs__nav-item-button {
      outline: 2px solid var(--color-white);

      &::after {
        width: 0;
        opacity: 0;
      }
    }

    .carousel-thumbs__progress {
      opacity: 0.5;
    }
  }

  .carousel-thumbs__nav-item:not(.swiper-slide-thumb-active):hover .carousel-thumbs__nav-item-img::before {
    border: 2px solid var(--color-white);
  }


}




/* Conversation HTML controls its height, including mobile flow. Keep original slide sizing otherwise. */
.carousel-thumbs__card.--conversation {
  align-self: flex-start;
  height: auto;
  aspect-ratio: auto;
  max-height: none;
}

.carousel-thumbs__card-desktop-video {
  display: none;
}

.carousel-thumbs__card-mobile-video {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: calc(300 / 348 * 100%);
  max-width: 620px;
  z-index: 1;
}

.carousel-thumbs__card-mobile-video :deep(.video) {
  border-radius: 0 !important;
}

.carousel-thumbs__card-mobile-video :deep(video) {
  width: 100% !important;
  border-radius: 7.3% 7.3% 0 0;
  overflow: hidden;
}

@media (min-width: 1024px) {
  .carousel-thumbs__card-desktop-video {
    display: block;
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 480px;
    transform: translateX(-50%);
    z-index: 1;
  }

  .carousel-thumbs__card-desktop-video :deep(.video) {
    border-radius: 0 !important;
  }

  .carousel-thumbs__card-desktop-video :deep(video) {
    width: 100% !important;
    border-radius: 7.3% 7.3% 0 0;
    overflow: hidden;
    
  }

  .carousel-thumbs__card-mobile-video {
    display: none;
  }
}

</style>
